import { applyFiltersToQuery } from '@/lib/stores/estate-filter';
import { supabase } from '@/lib/supabase/client';
import { OfferType } from '@/lib/supabase/estates/enums';
import type { Estate, EstatesSelectBuilder } from '@/types';

export interface SearchCriteria {
	id: string;
	title: string;
	created_at: string;
	filters: {
		offerType: string;
		estateType: string;
		locations: string[];
		minRooms: number;
		maxRooms: number;
		minArea: number;
		maxArea: number;
		minPrice: number;
		maxPrice: number;
		newBuilding: boolean | null;
	};
	labels: {
		offerType: string;
		estateType: string;
	};
}

export interface SearchCriteriaResults {
	count: number;
	firstEstate: Estate | null;
}

class SearchCriteriaService {
	private baseUrl: string;
	private endpoints: Record< string, string >;

	constructor() {
		this.baseUrl = '';
		this.endpoints = {
			searchCriteria: '/wp-json/dhsv/v1/search-criteria',
		};
	}

	/**
	 * Fetches all search criteria for the authenticated user.
	 * @param {string} token - Authorization token passed as 'X-Supabase-Token'.
	 * @return {Promise<SearchCriteria[]>} Array of search criteria.
	 */
	async getSearchCriteria( token: string ): Promise< SearchCriteria[] > {
		const url = `${ this.baseUrl }${ this.endpoints.searchCriteria }`;

		try {
			const response = await fetch( url, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'X-Supabase-Token': token,
				},
			} );

			if ( ! response.ok ) {
				throw new Error(
					`Failed to fetch search criteria: ${ response.statusText }`
				);
			}

			const data = await response.json();
			return Array.isArray( data ) ? data : [];
		} catch ( error: unknown ) {
			// eslint-disable-next-line no-console
			console.error(
				'Error fetching search criteria:',
				( error as Error ).message
			);
			throw error;
		}
	}

	/**
	 * Fetches a single search criteria by ID.
	 * @param {string} id    - The search criteria ID.
	 * @param {string} token - Authorization token passed as 'X-Supabase-Token'.
	 * @return {Promise<SearchCriteria>} The search criteria.
	 */
	async getSearchCriteriaById(
		id: string,
		token: string
	): Promise< SearchCriteria > {
		const url = `${ this.baseUrl }${ this.endpoints.searchCriteria }/${ id }`;

		try {
			const response = await fetch( url, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'X-Supabase-Token': token,
				},
			} );

			if ( ! response.ok ) {
				throw new Error(
					`Failed to fetch search criteria: ${ response.statusText }`
				);
			}

			return await response.json();
		} catch ( error: unknown ) {
			// eslint-disable-next-line no-console
			console.error(
				'Error fetching search criteria:',
				( error as Error ).message
			);
			throw error;
		}
	}

	/**
	 * Creates a new search criteria.
	 * @param {Object} filters - The search filters.
	 * @param {string} title   - The title of the search criteria (optional, will be auto-generated if empty).
	 * @param {string} token   - Authorization token passed as 'X-Supabase-Token'.
	 * @return {Promise<SearchCriteria>} The created search criteria.
	 */
	async createSearchCriteria(
		filters: SearchCriteria[ 'filters' ],
		title: string = '',
		token: string
	): Promise< SearchCriteria > {
		const url = `${ this.baseUrl }${ this.endpoints.searchCriteria }`;
		const payload = { filters, title };

		try {
			const response = await fetch( url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-Supabase-Token': token,
				},
				body: JSON.stringify( payload ),
			} );

			if ( ! response.ok ) {
				throw new Error(
					`Search criteria creation failed: ${ response.statusText }`
				);
			}

			const responseData = await response.json();

			// API returns an array with ID: [{"id":384791}]
			if (
				Array.isArray( responseData ) &&
				responseData.length > 0 &&
				responseData[ 0 ].id
			) {
				const newId = responseData[ 0 ].id;
				// Fetch the complete object
				return await this.getSearchCriteriaById( newId, token );
			}

			throw new Error( 'Invalid response format from API' );
		} catch ( error: unknown ) {
			// eslint-disable-next-line no-console
			console.error(
				'Error creating search criteria:',
				( error as Error ).message
			);
			throw error;
		}
	}

	/**
	 * Updates an existing search criteria.
	 * @param {string} id      - The search criteria ID.
	 * @param {Object} filters - The updated search filters.
	 * @param {string} title   - The updated title (optional, will be auto-generated if empty).
	 * @param {string} token   - Authorization token passed as 'X-Supabase-Token'.
	 * @return {Promise<SearchCriteria>} The updated search criteria.
	 */
	async updateSearchCriteria(
		id: string,
		filters: SearchCriteria[ 'filters' ],
		title: string = '',
		token: string
	): Promise< SearchCriteria > {
		const url = `${ this.baseUrl }${ this.endpoints.searchCriteria }/${ id }`;
		const payload = { filters, title };

		try {
			const response = await fetch( url, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					'X-Supabase-Token': token,
				},
				body: JSON.stringify( payload ),
			} );

			if ( ! response.ok ) {
				throw new Error(
					`Search criteria update failed: ${ response.statusText }`
				);
			}

			const responseData = await response.json();

			// Check if response is an array (like create) or a complete object
			if (
				Array.isArray( responseData ) &&
				responseData.length > 0 &&
				responseData[ 0 ].id
			) {
				// API returned array format, fetch the complete object
				return await this.getSearchCriteriaById(
					responseData[ 0 ].id,
					token
				);
			} else if ( responseData && responseData.id ) {
				// API returned complete object
				return responseData;
			}

			// If neither format, fetch the object using the provided ID
			return await this.getSearchCriteriaById( id, token );
		} catch ( error: unknown ) {
			// eslint-disable-next-line no-console
			console.error(
				'Error updating search criteria:',
				( error as Error ).message
			);
			throw error;
		}
	}

	/**
	 * Deactivates (not deletes) a search criteria by ID.
	 * @param {string} id    - The search criteria ID.
	 * @param {string} token - Authorization token passed as 'X-Supabase-Token'.
	 * @return {Promise<object>} API response after deactivation.
	 */
	async deleteSearchCriteria( id: string, token: string ): Promise< object > {
		const url = `${ this.baseUrl }${ this.endpoints.searchCriteria }/${ id }`;

		try {
			const response = await fetch( url, {
				method: 'DELETE',
				headers: {
					'Content-Type': 'application/json',
					'X-Supabase-Token': token,
				},
			} );

			if ( ! response.ok ) {
				throw new Error(
					`Search criteria deletion failed: ${ response.statusText }`
				);
			}

			return await response.json();
		} catch ( error: unknown ) {
			// eslint-disable-next-line no-console
			console.error(
				'Error deleting search criteria:',
				( error as Error ).message
			);
			throw error;
		}
	}

	/**
	 * Executes a search criteria to get result count and first estate.
	 * @param {SearchCriteria} searchCriteria - The search criteria to execute.
	 * @return {Promise<SearchCriteriaResults>} The search results.
	 */
	async executeSearchCriteria(
		searchCriteria: SearchCriteria
	): Promise< SearchCriteriaResults > {
		try {
			// Convert search criteria filters to filter format expected by applyFiltersToQuery
			const filters = {
				offerType:
					searchCriteria.filters.offerType === 'kauf'
						? OfferType.Sale
						: OfferType.Rent,
				estateTypes: searchCriteria.filters.estateType
					? [ searchCriteria.filters.estateType ]
					: [],
				locations: searchCriteria.filters.locations,
				minRooms: searchCriteria.filters.minRooms,
				maxRooms: searchCriteria.filters.maxRooms,
				minArea: searchCriteria.filters.minArea,
				maxArea: searchCriteria.filters.maxArea,
				minPrice: searchCriteria.filters.minPrice,
				maxPrice: searchCriteria.filters.maxPrice,
				sort: null,
				newBuilding: searchCriteria.filters.newBuilding ?? null,
			};

			// Create the base query
			let query = supabase
				.from( 'estates_flattened_with_images' )
				.select( '*', {
					count: 'exact',
				} ) as unknown as EstatesSelectBuilder;

			// Apply filters using the existing function
			query = applyFiltersToQuery( query, filters );

			// Get count and first result
			const { data, error, count } = await query.range( 0, 0 ); // Get only the first result

			if ( error ) {
				throw new Error( `Supabase query error: ${ error.message }` );
			}

			return {
				count: count || 0,
				firstEstate:
					data && data.length > 0 ? ( data[ 0 ] as Estate ) : null,
			};
		} catch ( error: unknown ) {
			// eslint-disable-next-line no-console
			console.error(
				'Error executing search criteria:',
				( error as Error ).message
			);
			throw error;
		}
	}
}

export default SearchCriteriaService;
